import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  KanbanSquare, Plus, LayoutGrid, Table2, Trash2, Pencil, MessageSquare,
  CalendarDays, User as UserIcon, Send, X, FolderKanban, Paperclip, Upload,
  Download, Clock, AlertTriangle, ListChecks,
} from "lucide-react";
import { tasksApi, projectsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = [
  { key: "todo", label: "Yapılacak", head: "bg-slate-100 text-slate-700" },
  { key: "in_progress", label: "Devam Ediyor", head: "bg-sky-100 text-sky-800" },
  { key: "done", label: "Tamamlandı", head: "bg-emerald-100 text-emerald-800" },
];
const PRIORITY = {
  low: { label: "Düşük", cls: "bg-slate-100 text-slate-600" },
  medium: { label: "Orta", cls: "bg-amber-100 text-amber-800" },
  high: { label: "Yüksek", cls: "bg-red-100 text-red-700" },
};
const EMPTY = { title: "", description: "", assignee_id: "", status: "todo", priority: "medium", due_date: "", project_id: "" };

export default function TaskBoard() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("board");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [quickFilter, setQuickFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const nameOf = (t) => t.assignee_name || users.find((u) => u.id === t.assignee_id)?.name || "—";
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (t) => t.due_date && t.due_date < today && t.status !== "done";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, u, p] = await Promise.all([tasksApi.list(), tasksApi.users(), projectsApi.list().catch(() => [])]);
      setTasks(t); setUsers(u); setProjects(p);
    } catch (e) { toast.error("Görevler yüklenemedi"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = tasks.filter((t) => {
    if (filterAssignee && t.assignee_id !== filterAssignee) return false;
    if (quickFilter === "mine") {
      const me = users.find((u) => u.email?.toLowerCase() === (user?.email || "").toLowerCase());
      if (!(t.assignee_email?.toLowerCase() === (user?.email || "").toLowerCase())) return false;
    }
    if (quickFilter === "overdue" && !isOverdue(t)) return false;
    return true;
  });
  const summary = {
    open: tasks.filter((t) => t.status !== "done").length,
    overdue: tasks.filter((t) => isOverdue(t)).length,
    done: tasks.filter((t) => t.status === "done").length,
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !detail) return;
    setUploading(true);
    try {
      const upd = await tasksApi.uploadAttachment(detail.id, file);
      setDetail(upd);
      setTasks((prev) => prev.map((x) => x.id === upd.id ? upd : x));
      toast.success("Dosya eklendi");
    } catch (err) { toast.error("Yüklenemedi"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t, assignee_id: t.assignee_id || "", project_id: t.project_id || "" }); setFormOpen(true); };

  const saveForm = async () => {
    if (!form.title.trim()) return toast.error("Başlık gerekli");
    const payload = { ...form, title: form.title.trim(), project_id: form.project_id || null };
    try {
      if (editing) { const upd = await tasksApi.update(editing.id, payload); toast.success("Görev güncellendi"); setDetail((d) => d && d.id === upd.id ? upd : d); }
      else { await tasksApi.create(payload); toast.success("Görev oluşturuldu"); }
      setFormOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const changeStatus = async (task, status) => {
    if (task.status === status) return;
    try {
      const upd = await tasksApi.setStatus(task.id, status);
      setTasks((prev) => prev.map((x) => x.id === task.id ? { ...x, status } : x));
      setDetail((d) => d && d.id === task.id ? { ...d, status } : d);
    } catch (e) { toast.error("Durum güncellenemedi"); }
  };

  const confirmDelete = async () => {
    try { await tasksApi.remove(deleteId); toast.success("Görev silindi"); setDeleteId(null); setDetail(null); load(); }
    catch (e) { toast.error("Silinemedi"); }
  };

  const sendComment = async () => {
    if (!comment.trim() || !detail) return;
    try {
      const upd = await tasksApi.addComment(detail.id, comment.trim());
      setDetail(upd); setComment("");
      setTasks((prev) => prev.map((x) => x.id === upd.id ? upd : x));
    } catch (e) { toast.error("Not eklenemedi"); }
  };

  const removeComment = async (cid) => {
    try {
      await tasksApi.removeComment(detail.id, cid);
      const upd = { ...detail, comments: detail.comments.filter((c) => c.id !== cid) };
      setDetail(upd); setTasks((prev) => prev.map((x) => x.id === upd.id ? upd : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const onDrop = (e, status) => { e.preventDefault(); const id = e.dataTransfer.getData("text/task"); const t = tasks.find((x) => x.id === id); if (t) changeStatus(t, status); };

  const Card = ({ t }) => (
    <div draggable onDragStart={(e) => e.dataTransfer.setData("text/task", t.id)}
      onClick={() => { setDetail(t); setComment(""); }}
      data-testid={`task-card-${t.id}`}
      className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm cursor-pointer hover:border-amber-400 hover:shadow transition-all">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900 leading-snug">{t.title}</p>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRIORITY[t.priority]?.cls}`}>{PRIORITY[t.priority]?.label}</span>
      </div>
      {t.project_name && <span className="mt-2 inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"><FolderKanban className="w-3 h-3" />{t.project_name}</span>}
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1 truncate max-w-[55%]"><UserIcon className="w-3 h-3" />{nameOf(t)}</span>
        <span className="inline-flex items-center gap-2">
          {t.due_date && <span className={`inline-flex items-center gap-0.5 ${isOverdue(t) ? "text-red-600 font-semibold" : ""}`}>{isOverdue(t) ? <AlertTriangle className="w-3 h-3" /> : <CalendarDays className="w-3 h-3" />}{t.due_date}</span>}
          {(t.attachments || []).length > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{t.attachments.length}</span>}
          {(t.comments || []).length > 0 && <span className="inline-flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{t.comments.length}</span>}
        </span>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 md:p-8 space-y-6" data-testid="task-board-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <KanbanSquare className="w-7 h-7 text-amber-600" /> Görevler
          </h1>
          <p className="text-sm text-slate-500 mt-1">Ekip için görev takibi, notlar ve iş yönetimi.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-700" data-testid="summary-open"><ListChecks className="w-3.5 h-3.5" /> Açık: {summary.open}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 font-semibold text-red-700" data-testid="summary-overdue"><AlertTriangle className="w-3.5 h-3.5" /> Geciken: {summary.overdue}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-700" data-testid="summary-done"><ListChecks className="w-3.5 h-3.5" /> Tamamlanan: {summary.done}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
            <button onClick={() => setView("board")} data-testid="view-board-btn"
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium ${view === "board" ? "bg-amber-600 text-white" : "text-slate-600"}`}>
              <LayoutGrid className="w-4 h-4" /> Pano
            </button>
            <button onClick={() => setView("table")} data-testid="view-table-btn"
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium ${view === "table" ? "bg-amber-600 text-white" : "text-slate-600"}`}>
              <Table2 className="w-4 h-4" /> Tablo
            </button>
          </div>
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
            {[["all", "Tümü"], ["mine", "Bana atananlar"], ["overdue", "Geciken"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setQuickFilter(k)} data-testid={`quick-${k}`}
                className={`rounded px-3 py-1.5 text-sm font-medium ${quickFilter === k ? "bg-slate-800 text-white" : "text-slate-600"}`}>{lbl}</button>
            ))}
          </div>
          <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} data-testid="filter-assignee"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500 bg-white">
            <option value="">Tüm atananlar</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {isAdmin && (
            <button onClick={openNew} data-testid="new-task-btn"
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors">
              <Plus className="w-4 h-4" /> Yeni Görev
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400">Yükleniyor…</div>
      ) : view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kanban-board">
          {STATUSES.map((col) => {
            const items = filtered.filter((t) => t.status === col.key);
            return (
              <div key={col.key} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, col.key)}
                data-testid={`column-${col.key}`}
                className="rounded-xl bg-slate-100/70 border border-slate-200 p-3 min-h-[200px]">
                <div className={`flex items-center justify-between rounded-md px-3 py-2 mb-3 text-sm font-bold ${col.head}`}>
                  <span>{col.label}</span><span className="text-xs">{items.length}</span>
                </div>
                <div className="space-y-2.5">
                  {items.map((t) => <Card key={t.id} t={t} />)}
                  {items.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Görev yok</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="task-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-100 text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Görev</th><th className="px-4 py-3">Proje</th>
                <th className="px-4 py-3">Atanan</th><th className="px-4 py-3">Öncelik</th>
                <th className="px-4 py-3">Termin</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3">Not</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="tasks-empty">Henüz görev yok.</td></tr>
              ) : filtered.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`task-row-${t.id}`}
                  onClick={() => { setDetail(t); setComment(""); }}>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">{t.title}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{t.project_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{nameOf(t)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY[t.priority]?.cls}`}>{PRIORITY[t.priority]?.label}</span></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{t.due_date || "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select value={t.status} onChange={(e) => changeStatus(t, e.target.value)} data-testid={`table-status-${t.id}`}
                      className="rounded border border-slate-200 px-2 py-1 text-xs bg-white outline-none focus:ring-2 focus:ring-amber-500">
                      {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400">{(t.comments || []).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Task form (create/edit) — admin only */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="task-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Görevi Düzenle" : "Yeni Görev"}</DialogTitle>
            <DialogDescription>Görev bilgilerini girin ve bir kullanıcıya atayın.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Başlık</Label>
              <Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="ör. Depo çatı metrajı çıkar" />
            </div>
            <div className="grid gap-2">
              <Label>Açıklama</Label>
              <textarea data-testid="task-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                placeholder="Detaylar…" className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500 resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Atanan kişi</Label>
                <select data-testid="task-assignee-select" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="">— Seçin —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role === "admin" ? " (yönetici)" : ""}</option>)}
                </select>
              </div>
              <div className="grid gap-2">
                <Label>İlgili proje</Label>
                <select data-testid="task-project-select" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="">— Yok —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid gap-2">
                <Label>Öncelik</Label>
                <select data-testid="task-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-amber-500">
                  {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="grid gap-2">
                <Label>Termin tarihi</Label>
                <Input data-testid="task-due-input" type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setFormOpen(false)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
            <button onClick={saveForm} data-testid="task-save-btn" className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700">Kaydet</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading pr-8">{detail.title}</DialogTitle>
                <DialogDescription>{detail.project_name ? `Proje: ${detail.project_name}` : "Görev detayları ve notlar"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {detail.description && <p className="text-sm text-slate-600 whitespace-pre-wrap">{detail.description}</p>}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${PRIORITY[detail.priority]?.cls}`}>{PRIORITY[detail.priority]?.label} öncelik</span>
                  <span className="inline-flex items-center gap-1 text-slate-500"><UserIcon className="w-3.5 h-3.5" />{nameOf(detail)}</span>
                  {detail.due_date && <span className="inline-flex items-center gap-1 text-slate-500"><CalendarDays className="w-3.5 h-3.5" />{detail.due_date}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Durum</Label>
                  <select value={detail.status} onChange={(e) => changeStatus(detail, e.target.value)} data-testid="detail-status-select"
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-amber-500">
                    {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                  {isAdmin && (
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={() => { setDetail(null); openEdit(detail); }} data-testid="detail-edit-btn" className="p-2 rounded-md text-slate-500 hover:bg-slate-100" title="Düzenle"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteId(detail.id)} data-testid="detail-delete-btn" className="p-2 rounded-md text-red-500 hover:bg-red-50" title="Sil"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>

                {/* Attachments */}
                <div className="border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Paperclip className="w-4 h-4 text-amber-600" /> Ekler ({(detail.attachments || []).length})</p>
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-attachment-btn"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                      <Upload className="w-3.5 h-3.5" /> {uploading ? "Yükleniyor…" : "Dosya ekle"}
                    </button>
                    <input ref={fileRef} type="file" onChange={uploadFile} accept="image/*,application/pdf" className="hidden" data-testid="attachment-input" />
                  </div>
                  <div className="space-y-1.5" data-testid="attachments-list">
                    {(detail.attachments || []).length === 0 && <p className="text-xs text-slate-400">Ek yok. Saha fotoğrafı, çizim veya PDF ekleyin.</p>}
                    {(detail.attachments || []).map((a) => (
                      <a key={a.id} href={tasksApi.attachmentUrl(detail.id, a.id)} target="_blank" rel="noreferrer" data-testid={`attachment-${a.id}`}
                        className="flex items-center gap-2 rounded-md bg-slate-50 border border-slate-100 px-3 py-2 hover:bg-slate-100 transition-colors">
                        <Paperclip className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="text-sm text-slate-700 truncate flex-1">{a.name}</span>
                        <span className="text-[10px] text-slate-400">{a.uploaded_by}</span>
                        <Download className="w-4 h-4 text-slate-400" />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Comments */}
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-amber-600" /> Notlar ({(detail.comments || []).length})</p>
                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="task-comments">
                    {(detail.comments || []).length === 0 && <p className="text-xs text-slate-400">Henüz not yok.</p>}
                    {(detail.comments || []).map((c) => (
                      <div key={c.id} className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2" data-testid={`comment-${c.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-700">{c.author_name || c.author_email}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-400">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                            {(isAdmin || c.author_email?.toLowerCase() === (user?.email || "").toLowerCase()) && (
                              <button onClick={() => removeComment(c.id)} data-testid={`delete-comment-${c.id}`} className="text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{c.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input value={comment} onChange={(e) => setComment(e.target.value)} data-testid="comment-input"
                      onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }}
                      placeholder="Not yaz…" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500" />
                    <button onClick={sendComment} data-testid="send-comment-btn" className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Activity timeline */}
                {(detail.activity || []).length > 0 && (
                  <div className="border-t border-slate-100 pt-3">
                    <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-600" /> Aktivite</p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1" data-testid="task-activity">
                      {(detail.activity || []).slice().reverse().map((a) => (
                        <div key={a.id} className="flex items-start gap-2 text-xs" data-testid={`activity-${a.id}`}>
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                          <p className="text-slate-500"><span className="font-semibold text-slate-700">{a.actor_name}</span> {a.text} <span className="text-slate-300">· {(a.created_at || "").slice(0, 16).replace("T", " ")}</span></p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Görev silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu görev ve tüm notları kalıcı olarak silinir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-task" className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
